/**
 * Lightweight, dependency-free spam signals for the lead form. This is
 * deliberately not a CAPTCHA or a third-party anti-spam service (out of
 * scope here) - it's the set of cheap, well-established heuristics that
 * catch the overwhelming majority of automated form spam without adding
 * friction for real homeowners: a honeypot field, a minimum fill time, and
 * a couple of content checks. All signals are internal-only (see
 * docs/lead-capture.md) - a flagged submission is never told why.
 */
export interface SpamCheckInput {
  /** Value of a hidden field real users never see or fill; any value here means a bot filled every field blindly. */
  honeypotValue?: string;
  /** ISO 8601 timestamp of when the form was first rendered/shown to the user. */
  formRenderedAt: string;
  /** ISO 8601 timestamp of the submission attempt. */
  submittedAt: string;
  name: string;
  email?: string;
  projectDescription?: string;
}

export interface SpamCheckOptions {
  /** Submissions faster than this after the form rendered are flagged - no human reads and fills a multi-field form this quickly. Default 2500ms. */
  minSubmitDelayMs?: number;
  /** More than this many URLs in the free-text description is flagged. Default 2. */
  maxLinksInDescription?: number;
  /** Email domains treated as disposable/throwaway. Extend via options rather than editing this module. */
  disposableEmailDomains?: string[];
}

export interface SpamCheckResult {
  isLikelySpam: boolean;
  reasons: string[];
}

const DEFAULT_MIN_SUBMIT_DELAY_MS = 2500;
const DEFAULT_MAX_LINKS_IN_DESCRIPTION = 2;
const DEFAULT_DISPOSABLE_EMAIL_DOMAINS = ["mailinator.com", "tempmail.com", "guerrillamail.com", "10minutemail.com"];

const URL_PATTERN_SOURCE = "https?:\\/\\/\\S+|www\\.\\S+";

function countLinks(text: string): number {
  return text.match(new RegExp(URL_PATTERN_SOURCE, "gi"))?.length ?? 0;
}

function containsUrl(text: string): boolean {
  return new RegExp(URL_PATTERN_SOURCE, "i").test(text);
}

export function evaluateSpamSignals(input: SpamCheckInput, options: SpamCheckOptions = {}): SpamCheckResult {
  const minSubmitDelayMs = options.minSubmitDelayMs ?? DEFAULT_MIN_SUBMIT_DELAY_MS;
  const maxLinksInDescription = options.maxLinksInDescription ?? DEFAULT_MAX_LINKS_IN_DESCRIPTION;
  const disposableEmailDomains = options.disposableEmailDomains ?? DEFAULT_DISPOSABLE_EMAIL_DOMAINS;

  const reasons: string[] = [];

  if (input.honeypotValue && input.honeypotValue.trim().length > 0) {
    reasons.push("honeypot_field_filled");
  }

  const renderedAt = Date.parse(input.formRenderedAt);
  const submittedAt = Date.parse(input.submittedAt);
  if (Number.isFinite(renderedAt) && Number.isFinite(submittedAt)) {
    const elapsedMs = submittedAt - renderedAt;
    if (elapsedMs < minSubmitDelayMs) {
      reasons.push("submitted_too_quickly");
    }
  }

  if (containsUrl(input.name)) {
    reasons.push("url_in_name");
  }

  if (input.projectDescription && countLinks(input.projectDescription) > maxLinksInDescription) {
    reasons.push("excessive_links_in_description");
  }

  if (input.email) {
    const domain = input.email.split("@")[1]?.toLowerCase();
    if (domain && disposableEmailDomains.includes(domain)) {
      reasons.push("disposable_email_domain");
    }
  }

  return { isLikelySpam: reasons.length > 0, reasons };
}
